// src/components/Inbox/InboxModal.jsx
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Bell,
  CalendarClock,
  CheckCircle2,
  CreditCard,
  MessageCircle,
  Package,
  ShoppingBag,
  X,
} from "lucide-react";
import {
  useNotifications,
  useNotificationsForWorld,
} from "../../contexts/NotificationsContext";

// Map notification types to icons
const TYPE_ICON = {
  offer: ShoppingBag,
  message: MessageCircle,
  booking: CalendarClock,
  task: CalendarClock,
  "shop-message": ShoppingBag,
  "shop-order-status": Package,
  "workshop-booking": CalendarClock,
  "workshop-message": MessageCircle,
  "studio-space-booking": CalendarClock,
  "studio-space-message": MessageCircle,
  "forum-reply": MessageCircle,
  "forum-mention": MessageCircle,
  "forum-message": MessageCircle,
  "direct-message": MessageCircle,
  "system-billing": CreditCard,
  "system-app": Bell,
};

const severityBadge = {
  success: "text-green-300 border-green-400/30",
  warning: "text-amber-300 border-amber-400/30",
  error: "text-red-300 border-red-400/30",
  info: "text-slate-200 border-white/10",
};

function timeAgo(iso) {
  if (!iso) return "";
  const delta = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(delta / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 4) return `${weeks}w ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

/**
 * InboxModal
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - world?: "studio" | "visitor" (optional – can be used for filtering later)
 */
export default function InboxModal({ open, onClose, world }) {
  const [filter, setFilter] = useState("all");
  const navigate = useNavigate();
  const baseCtx = useNotifications() || {};
  const scopedCtx = useNotificationsForWorld(world || "studio");
  const ctx = world ? scopedCtx : baseCtx;
  const { notifications = [], loading, markRead, markAllRead } = ctx;

  // Unread count (hook likely returns camelCase readAt/createdAt)
  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.readAt && !n.read).length,
    [notifications]
  );

  const sorted = useMemo(() => {
    const list = notifications || [];
    const filtered =
      filter === "unread" ? list.filter((n) => !n.readAt && !n.read) : list;
    return [...filtered].sort(
      (a, b) =>
        new Date(b.createdAt || b.created_at || 0).getTime() -
        new Date(a.createdAt || a.created_at || 0).getTime()
    );
  }, [notifications, filter]);

  if (!open) {
    return null;
  }

  const offerNotifications = useMemo(
    () => (notifications || []).filter((n) => n.type === "offer"),
    [notifications]
  );
  const hasOffers = offerNotifications.length > 0;
  const baseList = useMemo(
    () => (sorted || []).filter((n) => n.type !== "offer"),
    [sorted]
  );

  const handleSelect = (notification) => {
    markRead?.(notification.id);
    const targetRoute = notification.route || notification.target?.route;
    if (targetRoute) {
      navigate(targetRoute);
      onClose?.();
    }
  };

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-[1400] bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        aria-label="Close inbox"
      />

      {/* Panel */}
      <div className="fixed inset-0 z-[1410] flex items-start justify-center overflow-y-auto p-4">
        <div className="mt-12 w-full max-w-3xl rounded-2xl border border-white/10 bg-neutral-900/95 shadow-2xl backdrop-blur-xl text-white">
          {/* Header */}
          <header className="flex items-center justify-between border-b border-white/10 px-6 py-4">
            <div>
              <h2 className="text-2xl font-semibold">Inbox</h2>
              <p className="text-sm text-white/60">
                {loading
                  ? "Loading..."
                  : unreadCount > 0
                  ? `${unreadCount} unread`
                  : hasOffers
                  ? "Offers waiting"
                  : "All caught up"}
              </p>
            </div>
            <button
              className="rounded-full border border-white/10 bg-white/5 p-2 text-white/80 hover:text-white"
              onClick={onClose}
              aria-label="Close inbox"
            >
              <X size={18} />
            </button>
          </header>

          {/* Filters */}
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/5 px-6 py-3 text-sm">
            <div className="flex items-center gap-2">
              <button
                className="rounded-full border border-white/10 px-3 py-1 text-xs uppercase tracking-wide text-white/70 hover:text-white"
                onClick={markAllRead}
              >
                Mark all as read
              </button>
              <button
                className={`rounded-full px-3 py-1 text-xs uppercase tracking-wide ${
                  filter === "all"
                    ? "bg-orange-500 text-black"
                    : "border border-white/10 text-white/70 hover:text-white"
                }`}
                onClick={() => setFilter("all")}
              >
                All
              </button>
              <button
                className={`rounded-full px-3 py-1 text-xs uppercase tracking-wide ${
                  filter === "unread"
                    ? "bg-orange-500 text-black"
                    : "border border-white/10 text-white/70 hover:text-white"
                }`}
                onClick={() => setFilter("unread")}
              >
                Unread
              </button>
            </div>
          </div>

          {/* List */}
          <div className="max-h-[70vh] overflow-y-auto px-6 py-4">
            {sorted.length === 0 && !loading && !hasOffers && (
              <div className="rounded-xl border border-white/10 bg-white/5 px-4 py-10 text-center text-sm text-white/60">
                Nothing to see here yet. Notifications from shop, bookings,
                forums, and billing will appear as they happen.
              </div>
            )}

            {hasOffers && (
              <div className="mb-4 rounded-xl border border-white/10 bg-white/5 px-4 py-3">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-white/60">
                  Shop offers
                </div>
                <div className="space-y-2">
                  {offerNotifications.map((offer) => (
                    <button
                      key={offer.id}
                      className="w-full rounded-lg border border-white/10 bg-neutral-900/70 px-3 py-2 text-left text-sm hover:border-orange-400/60 hover:bg-neutral-800/80 transition"
                      onClick={() => {
                        const targetRoute = offer.route || offer.target?.route || "/dashboard/shop";
                        navigate(targetRoute);
                        onClose?.();
                      }}
                    >
                      <div className="text-xs text-white/60">
                        {offer?.title || "New offer"}{" "}
                        {offer?.meta?.listingTitle ? `on ${offer.meta.listingTitle}` : ""}
                      </div>
                      <div className="text-sm text-white">
                        {offer?.meta?.amount_cents != null
                          ? `$${(Number(offer.meta.amount_cents) / 100).toFixed(0)}`
                          : ""}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-3">
              {baseList.map((notification) => {
                const Icon =
                  TYPE_ICON[notification.type] ||
                  (notification.severity === "warning"
                    ? AlertTriangle
                    : notification.severity === "error"
                    ? AlertTriangle
                    : notification.severity === "success"
                    ? CheckCircle2
                    : Bell);

                const badgeClass =
                  severityBadge[notification.severity || "info"] ||
                  severityBadge.info;

                const unread = !notification.readAt && !notification.read;

                return (
                  <button
                    key={notification.id}
                    className={`w-full rounded-2xl border px-4 py-3 text-left transition ${
                      unread
                        ? "border-orange-400/40 bg-orange-400/10"
                        : "border-white/10 bg-white/5"
                    } hover:border-orange-400/60`}
                    onClick={() => handleSelect(notification)}
                  >
                    <div className="flex items-start gap-3">
                      <span
                        className={`inline-flex h-9 w-9 items-center justify-center rounded-xl border ${badgeClass}`}
                      >
                        <Icon size={18} />
                      </span>
                      <div className="flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-base font-semibold">
                            {notification.title}
                          </p>
                          <span className="text-xs text-white/60">
                            {timeAgo(notification.createdAt || notification.created_at)}
                          </span>
                        </div>
                        <p
                          className={`text-sm ${
                            unread ? "text-white/90" : "text-white/70"
                          }`}
                        >
                          {notification.body}
                        </p>
                        {(notification.entityType || notification.target?.entity) && (
                          <p className="mt-1 text-xs text-white/50">
                            {String(notification.entityType || notification.target?.entity).replace("-", " ")}
                          </p>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
